import { createFileRoute, Link } from "@tanstack/react-router";
import { Moon, Sun, Sunrise, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/weekend-getaway-crystal-beach-lake-erie")({
  head: () => ({
    meta: [
      { title: "Weekend Getaway to Crystal Beach - Malak BnB Resort" },
      { name: "description", content: "A two day itinerary for a Crystal Beach weekend on Lake Erie, Friday arrival to Sunday checkout, with Malak BnB Resort as your base." },
      { property: "og:title", content: "Weekend Getaway to Crystal Beach - Malak BnB Resort" },
      { property: "og:description", content: "Friday to Sunday in Crystal Beach: Bay Beach, cycling, a slow dinner, and a pool that stays warm past dark." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/weekend-getaway-crystal-beach-lake-erie" },
      { property: "og:image", content: "/images/beach-bay.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Weekend Getaway to Crystal Beach", path: "/blog/weekend-getaway-crystal-beach-lake-erie" },
        ]),
      },
    ],
    links: [canonical("/blog/weekend-getaway-crystal-beach-lake-erie")],
  }),
  component: WeekendGetawayPage,
});

const FAQ = [
  {
    q: "How many days do you need for a Crystal Beach weekend getaway?",
    a: "Two nights is enough to properly experience Crystal Beach: a Friday arrival, a full Saturday for the beach and town, and a relaxed Sunday morning before checkout. Malak BnB Resort is five minutes from Bay Beach, so no time gets lost driving between the beach and where you are staying.",
  },
  {
    q: "Is Crystal Beach a good weekend trip from Toronto or Buffalo?",
    a: "Yes. Crystal Beach is roughly a two hour drive from Toronto and about 15 minutes from Buffalo, New York, across the Peace Bridge. It works well as a short weekend escape from either city without the longer drive to Niagara Falls' busier core.",
  },
  {
    q: "What should I pack for a weekend at Malak BnB Resort?",
    a: "A swimsuit for the outdoor heated pool and Bay Beach, comfortable shoes for walking or cycling around town, and something for a relaxed dinner out. Malak BnB Resort provides towels, air conditioning, and free Wi-Fi in every room.",
  },
  {
    q: "Do I need a car for a Crystal Beach weekend?",
    a: "Not for the core of the weekend. Bay Beach, the town centre, and several restaurants are within walking distance of Malak BnB Resort. A car helps if you want to add a Niagara Falls day trip or explore Fort Erie and Ridgeway further afield.",
  },
];

function WeekendGetawayPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/beach-bay.webp"
          alt="Bay Beach at Crystal Beach, Ontario at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Weekend Getaway</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">A Two Day Itinerary</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              A Weekend Getaway to Crystal Beach and Lake Erie
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              There is a particular rhythm to a good weekend away, and Crystal Beach falls into it easily. You do
              not need a long list of attractions or a tight schedule. You need a beach, somewhere comfortable to
              come back to, and a couple of good meals in between.
            </p>
            <p>
              This is a two night version of that weekend: Friday evening arrival, a full Saturday that moves
              between the water and the town, and a Sunday morning that does not feel rushed. Malak BnB Resort sits
              at the centre of it, five minutes on foot from Bay Beach, which is close enough that the whole trip
              works without much driving at all.
            </p>
          </div>
        </div>
      </section>

      {/* Friday */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img src="/images/gallery-bedroom-warm.webp" alt="Warmly lit bedroom at Malak BnB Resort in the evening" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-3">
                <Moon className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Friday Evening</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Arrival and Settling In
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Aim to arrive at Malak BnB Resort before dinner. Check in, drop the bags, and take the short walk
                down to Bay Beach while there is still light left. Nothing structured, just enough to feel like the
                week is actually over. Come back for a quiet evening, or head into town for a first dinner before
                settling in for the night.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Saturday day */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sun className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Saturday</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Beach, Bike, and a Slow Afternoon
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Start with breakfast at Malak, then head to Bay Beach while the morning is still cool. By midday,
                either stay put with a book and the sand, or rent a bike and ride the flat streets around town.
                Crystal Beach rewards a slow pace more than a packed schedule. Come back to the property in the
                afternoon for the outdoor heated pool if the beach crowd has picked up.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/explore-biking.webp" alt="Cycling through the streets of Crystal Beach, Ontario" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Saturday night */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img src="/images/gallery-pool-outdoor.webp" alt="Outdoor heated pool at Malak BnB Resort at dusk" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-3">
                <Moon className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Saturday Night</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Dinner Out, Then the Pool
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Crystal Beach and nearby Ridgeway have a good spread of casual restaurants within a short walk or
                drive. Take your time over dinner, then come back for a late swim. The pool at Malak stays heated
                well into the evening, and a night swim is one of those small things that makes a weekend feel
                complete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sunday */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Sunrise className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">Sunday</p>
          </div>
          <h2 className="text-3xl font-heading text-foreground mb-6">One Last Walk Before Checkout</h2>
          <p className="text-muted-foreground leading-relaxed">
            Breakfast is included at Malak BnB Resort, so there is no need to rush out for coffee. Take one more
            short walk to Bay Beach if the morning is clear, then check out with the kind of unhurried feeling that
            a short trip does not always leave you with. If you have the time, Niagara Falls is a 30 minute drive
            and makes an easy add-on before heading home.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Planning Your Weekend</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/blog/things-to-do-near-crystal-beach-ontario" className="text-primary font-semibold text-sm hover:underline">
            See the full guide to things to do near Crystal Beach →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Plan your weekend around Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <Link to="/rooms" className="inline-flex items-center justify-center rounded-full border border-primary-foreground/20 text-primary-foreground px-10 py-4 text-sm font-semibold hover:bg-primary-foreground/10 transition-colors">
              View Rooms and Rates
            </Link>
          </div>
          <a href="tel:+12899298055" className="mt-6 inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
            <Phone className="h-4 w-4" />
            1-289-929-8055
          </a>
        </div>
      </section>
    </div>
  );
}
